<template>
  <div>
    <div class="back-row">
      <button class="btn-back" @click="$router.go(-1)">
        <font-awesome-icon icon="arrow-left" /> Back
      </button>
    </div>

    <div class="page-header">
      <div>
        <h5 class="page-title">{{ isEdit ? 'Edit Purchase Order' : 'New Purchase Order' }}</h5>
        <p class="page-sub" v-if="isFromPR && sourcePR">
          From PR <strong>{{ sourcePR.pr_number }}</strong> — {{ sourcePR.title }}
        </p>
        <p class="page-sub" v-else>Fill in the order details and confirm line items</p>
      </div>
      <span v-if="isEdit && form.po_number" class="mono">{{ form.po_number }}</span>
    </div>

    <div v-if="pageLoading" class="loading-state">
      <div class="spinner"></div><p>Loading...</p>
    </div>

    <template v-else>
      <!-- ── Order Details ─────────────────────────────── -->
      <div class="form-card">
        <h6 class="section-title">Order Details</h6>
        <div class="fields-grid">
          <div class="field-group field-full" v-if="!isFromPR">
            <label class="field-label">Title / Summary <span class="req">*</span></label>
            <input v-model="form.title" class="field-input" :class="{ 'field-input--error': errors.title }" placeholder="e.g. Office Supplies — Q3 2026" />
            <span class="field-error" v-if="errors.title">{{ errors.title }}</span>
          </div>

          <div class="field-group">
            <label class="field-label">Vendor <span v-if="isFromPR" class="req">*</span></label>
            <select v-model="form.vendor_id" class="field-input field-select" :class="{ 'field-input--error': errors.vendor_id }">
              <option value="">Select vendor{{ isFromPR ? '' : ' (optional)' }}</option>
              <option :value="v.id" v-for="v in vendors" :key="v.id">{{ v.name }}</option>
            </select>
            <span class="field-error" v-if="errors.vendor_id">{{ errors.vendor_id }}</span>
          </div>

          <div class="field-group">
            <label class="field-label">Currency</label>
            <select v-model="form.currency" class="field-input field-select">
              <option v-for="c in currencies" :key="c.id" :value="c.code || c.name">
                {{ c.name }}{{ c.code ? ` (${c.code})` : '' }}
              </option>
            </select>
          </div>

          <div class="field-group">
            <label class="field-label">Expected Delivery Date</label>
            <input v-model="form.expected_delivery_date" type="date" class="field-input" />
          </div>

          <div class="field-group">
            <label class="field-label">Payment Terms</label>
            <select v-model="form.payment_term_id" class="field-input field-select" @change="onPaymentTermChange">
              <option value="">— Select payment terms —</option>
              <option v-for="t in paymentTerms" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </div>

          <div class="field-group">
            <label class="field-label">Item Type</label>
            <select v-model="form.item_type" class="field-input field-select">
              <option value="goods">Goods (Inventory)</option>
              <option value="service">Service</option>
              <option value="expense">Expense</option>
            </select>
          </div>

          <div class="field-group">
            <label class="field-label">Destination Location</label>
            <Multiselect
              v-model="form.location_id"
              label="name"
              :options="locationOptions"
              :searchable="true"
              :close-on-select="true"
              :create-option="false"
              placeholder="Search location..."
              class="custom-multiselect"
              @change="applyHeaderLocation"
            />
          </div>

          <div class="field-group field-full">
            <label class="field-label">Delivery Address</label>
            <textarea v-model="form.delivery_address" class="field-input field-textarea" rows="2" placeholder="Full delivery address..."></textarea>
          </div>

          <div class="field-group field-full">
            <label class="field-label">Description / Instructions</label>
            <div class="quill-wrap">
              <QuillEditor
                v-model:content="form.description"
                content-type="html"
                :toolbar="descToolbar"
                placeholder="Optional notes to vendor..."
                theme="snow"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- ── PR Item Selection (PR mode) ──────────────── -->
      <template v-if="isFromPR">
        <div class="form-card">
          <div class="section-header">
            <h6 class="section-title">Select Items to Order</h6>
            <span class="selected-count" v-if="selectedIds.length">
              {{ selectedIds.length }} item{{ selectedIds.length !== 1 ? 's' : '' }} selected
            </span>
          </div>
          <span class="field-error mb-8" v-if="errors.lines">{{ errors.lines }}</span>

          <div class="lines-table-wrap">
            <table class="lines-table">
              <thead>
                <tr>
                  <th class="th-check">
                    <input type="checkbox" class="cb" v-model="allAvailableSelected"
                           :title="allAvailableSelected ? 'Deselect all' : 'Select all available'" />
                  </th>
                  <th>Item</th>
                  <th>Qty to Order</th>
                  <th>UOM</th>
                  <th class="text-right">Unit Price</th>
                  <th class="text-right">Line Total</th>
                  <th>Destination</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="line in prLines" :key="line.id"
                    :class="{ 'row-ordered': line.status === 'ordered', 'row-selected': selectedIds.includes(line.id) }">
                  <td class="td-check">
                    <input type="checkbox" class="cb"
                           :disabled="line.status === 'ordered'"
                           :checked="selectedIds.includes(line.id)"
                           @change="toggleLine(line)" />
                  </td>
                  <td>
                    <div class="item-desc">{{ line.description }}</div>
                    <div class="item-spec" v-if="line.specification">{{ line.specification }}</div>
                  </td>
                  <td class="td-qty">
                    <input v-if="selectedIds.includes(line.id)"
                           v-model.number="lineQtys[line.id]"
                           type="number" min="0.001" :max="availableQty(line)"
                           step="any" class="cell-input cell-num"
                           @blur="capQty(line)" />
                    <span v-else class="qty-dim">{{ formatNum(availableQty(line)) }}</span>
                  </td>
                  <td>{{ line.uom || '—' }}</td>
                  <td class="td-price">
                    <input v-if="selectedIds.includes(line.id)"
                           v-model.number="linePrices[line.id]"
                           type="number" min="0" step="any"
                           class="cell-input cell-num" />
                    <span v-else class="qty-dim">{{ formatNum(line.estimated_unit_cost || 0) }}</span>
                  </td>
                  <td class="text-right">{{ formatNum(prLineTotal(line)) }}</td>
                  <td>
                    <select v-model="lineLocations[line.id]"
                            class="cell-input cell-select"
                            :class="{ 'cell-select--locked': sourcePR && sourcePR.location_id }"
                            :disabled="!!(sourcePR && sourcePR.location_id)">
                      <option :value="undefined">— Location</option>
                      <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.store_name }}</option>
                    </select>
                  </td>
                  <td>
                    <span :class="['line-status', `line-status--${line.status || 'available'}`]">
                      {{ lineStatusLabel(line.status) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="!prLines.length" class="empty-lines">This PR has no items.</div>
          </div>
        </div>
      </template>

      <!-- ── Manual Line Items (non-PR mode) ─────────── -->
      <template v-else>
        <div class="form-card">
          <div class="section-header">
            <h6 class="section-title">Line Items</h6>
            <button class="btn-add-line" @click="addLine">
              <font-awesome-icon icon="plus" /> Add Line
            </button>
          </div>

          <div v-if="errors.lines" class="field-error mb-8">{{ errors.lines }}</div>

          <div class="lines-table-wrap">
            <table class="lines-table">
              <colgroup v-if="isGoods">
                <col style="width:28%"><col style="width:12%"><col style="width:7%">
                <col style="width:7%"><col style="width:11%"><col style="width:11%">
                <col style="width:18%"><col style="width:6%">
              </colgroup>
              <colgroup v-else>
                <col style="width:38%"><col style="width:16%"><col style="width:9%">
                <col style="width:9%"><col style="width:13%"><col style="width:13%">
                <col style="width:2%">
              </colgroup>
              <thead>
                <tr>
                  <th>Description</th><th>Specification</th><th>Qty</th>
                  <th>UOM</th><th>Unit Price</th><th class="text-right">Total</th>
                  <th v-if="isGoods">Product <span class="req">*</span></th><th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(line, i) in form.lines" :key="i">
                  <td>
                    <input v-model="line.description" class="cell-input"
                           :class="{ 'cell-input--error': lineErrors[i]?.description }" placeholder="Item description" />
                  </td>
                  <td><input v-model="line.specification" class="cell-input" placeholder="Spec (optional)" /></td>
                  <td>
                    <input v-model.number="line.quantity" type="number" min="0.0001" step="any"
                           class="cell-input cell-num" @input="recalcLine(i)" />
                  </td>
                  <td><input v-model="line.uom" class="cell-input" placeholder="pcs" list="uom-list" /></td>
                  <td>
                    <input v-model.number="line.unit_price" type="number" min="0" step="any"
                           class="cell-input cell-num" @input="recalcLine(i)" />
                  </td>
                  <td class="cell-total">{{ formatNum(lineTotal(line)) }}</td>
                  <td v-if="isGoods" class="cell-product">
                    <Multiselect
                      v-model="line.product_id"
                      label="name"
                      :options="productOptions"
                      :searchable="true"
                      :close-on-select="true"
                      :create-option="false"
                      :hideSelected="false"
                      placeholder="Search product..."
                      class="cell-multiselect"
                      :class="{ 'cell-multiselect--error': lineErrors[i]?.product_id }"
                      @change="val => onLineProductChange(line, val)"
                    />
                  </td>
                  <td>
                    <button class="btn-remove-line" @click="removeLine(i)" title="Remove">
                      <font-awesome-icon icon="times" />
                    </button>
                  </td>
                </tr>
                <tr v-for="(line, i) in form.lines" :key="`loc-${i}`" class="line-loc-row">
                  <td :colspan="isGoods ? 5 : 4" class="loc-label-cell">
                    <span class="loc-label"><font-awesome-icon icon="map-marker-alt" class="me-1" />Destination location for line {{ i + 1 }}</span>
                  </td>
                  <td :colspan="isGoods ? 3 : 3">
                    <Multiselect
                      v-model="line.location_id"
                      label="name"
                      :options="locationOptions"
                      :searchable="true"
                      :close-on-select="true"
                      :create-option="false"
                      placeholder="Search location..."
                      class="cell-multiselect"
                    />
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="tfoot-row">
                  <td :colspan="isGoods ? 4 : 3"></td>
                  <td class="tfoot-label">Subtotal</td>
                  <td class="tfoot-val">{{ formatNum(subtotal) }}</td>
                  <td v-if="isGoods"></td>
                </tr>
                <tr class="tfoot-row">
                  <td :colspan="isGoods ? 3 : 2"></td>
                  <td colspan="2" class="tfoot-tax-row">
                    <span class="tfoot-label-inline">Tax / VAT</span>
                    <div class="tax-input-group">
                      <input v-model.number="form.tax_rate" type="number" min="0" max="100" step="0.01"
                             class="cell-input cell-num tax-rate-input" placeholder="0.00" />
                      <span class="tax-pct-sign">%</span>
                    </div>
                  </td>
                  <td class="tfoot-val">{{ formatNum(computedTax) }}</td>
                  <td v-if="isGoods"></td>
                </tr>
                <tr class="tfoot-total-row">
                  <td :colspan="isGoods ? 4 : 3"></td>
                  <td class="tfoot-label">Total</td>
                  <td class="tfoot-val tfoot-grand">{{ formatNum(grandTotal) }}</td>
                  <td v-if="isGoods"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </template>

      <!-- ── Notes ────────────────────────────────────── -->
      <div class="form-card">
        <h6 class="section-title">Internal Notes</h6>
        <textarea v-model="form.notes" class="field-input field-textarea" rows="3"
                  placeholder="Any internal notes (not visible to vendor)..."></textarea>
      </div>

      <!-- ── PR mode totals ────────────────────────────── -->
      <div v-if="isFromPR" class="form-card totals-card">
        <div class="totals-row">
          <span class="totals-label">Subtotal</span>
          <span class="totals-val">{{ formatNum(subtotal) }}</span>
        </div>
        <div class="totals-row">
          <span class="totals-label">Tax / VAT</span>
          <div class="totals-tax-right">
            <div class="tax-input-group">
              <input v-model.number="form.tax_rate" type="number" min="0" max="100" step="0.01"
                     class="tax-inline-input" placeholder="0.00" />
              <span class="tax-pct-sign">%</span>
            </div>
            <span class="totals-tax-computed">= {{ formatNum(computedTax) }}</span>
          </div>
        </div>
        <div class="totals-row totals-grand-row">
          <span class="totals-label-grand">Total</span>
          <span class="totals-val-grand">{{ formatNum(grandTotal) }}</span>
        </div>
      </div>

      <!-- ── Actions ───────────────────────────────────── -->
      <div class="form-actions">
        <button class="btn-outline" @click="$router.go(-1)" :disabled="!!saving">Cancel</button>
        <button class="btn-primary" :disabled="!!saving || (isFromPR && !selectedIds.length)" @click="savePO">
          <span v-if="saving === 'save'" class="spinner-sm"></span>
          {{ saving === 'save' ? 'Saving...' : (isEdit ? 'Update Order' : 'Create Purchase Order') }}
        </button>
      </div>
    </template>

    <datalist id="uom-list">
      <option v-for="u in uoms" :key="u.id" :value="u.name" />
    </datalist>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import Multiselect from '@vueform/multiselect';
import '@vueform/multiselect/themes/default.css';

const emptyLine = () => ({ description: '', specification: '', quantity: 1, uom: '', unit_price: 0, pr_line_id: null, product_id: null, location_id: null });

export default {
  components: { QuillEditor, Multiselect },
  setup() { return { toast: useToast() }; },

  data() {
    return {
      pageLoading: false,
      saving:      false,
      sourcePR:    null,
      prLines:     [],
      selectedIds: [],
      lineQtys:    {},
      linePrices:  {},
      vendors:        [],
      currencies:     [],
      uoms:           [],
      products:       [],
      locations:      [],
      paymentTerms:   [],
      lineLocations:  {},
      form: {
        title: '', vendor_id: '', currency: 'NGN',
        expected_delivery_date: '', payment_terms: '', payment_term_id: '',
        delivery_address: '', description: '', notes: '',
        tax_rate: '',
        tax_amount: 0,
        item_type: 'goods',
        location_id: null,
        lines: [emptyLine()],
        pr_id: null, po_number: '',
      },
      errors:     {},
      lineErrors: [],
      descToolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
      ],
    };
  },

  computed: {
    isEdit()   { return !!this.$route.params.id; },
    isFromPR() { return !this.isEdit && !!this.$route.query.pr_id; },
    isGoods()  { return this.form.item_type === 'goods'; },

    locationOptions() {
      return this.locations.map(l => ({ value: l.id, name: l.store_name, address: l.address }));
    },

    productOptions() {
      return this.products.map(p => ({ value: p.id, name: p.product_name, uom: p.unit_of_measurement }));
    },

    availablePRLines() {
      return this.prLines.filter(l => l.status !== 'ordered');
    },

    selectedPRLines() {
      return this.prLines.filter(l => this.selectedIds.includes(l.id));
    },

    allAvailableSelected: {
      get() {
        return this.availablePRLines.length > 0 &&
               this.availablePRLines.every(l => this.selectedIds.includes(l.id));
      },
      set(val) {
        if (val) {
          this.selectedIds = this.availablePRLines.map(l => l.id);
        } else {
          this.selectedIds = [];
        }
      },
    },

    subtotal() {
      if (this.isFromPR) {
        return this.selectedPRLines.reduce((sum, l) => {
          const qty   = parseFloat(this.lineQtys[l.id] ?? this.availableQty(l));
          const price = parseFloat(this.linePrices[l.id] ?? l.estimated_unit_cost ?? 0);
          return sum + (qty * price);
        }, 0);
      }
      return this.form.lines.reduce((s, l) => s + this.lineTotal(l), 0);
    },

    computedTax() {
      const rate = Number(this.form.tax_rate) || 0;
      return rate > 0 ? (this.subtotal * rate) / 100 : (Number(this.form.tax_amount) || 0);
    },
    grandTotal() { return this.subtotal + this.computedTax; },
  },

  watch: {
    'form.item_type'(newType) {
      if (newType !== 'goods') {
        this.form.lines.forEach(l => { l.product_id = null; });
      }
    },
  },

  async created() {
    this.pageLoading = true;
    try {
      const prId = this.$route.query.pr_id;
      const [vendorsRes, currRes, uomRes, productsRes, locationsRes, termsRes] = await Promise.all([
        axios.get('/procurement/vendors', { params: { per_page: 200 } }),
        axios.get('/settings/currency'),
        axios.get('/settings/unit-of-measurement'),
        axios.get('/inventory/product/active/all'),
        axios.get('/admin/store/all'),
        axios.get('/procurement/payment-terms').catch(() => ({ data: { data: [] } })),
      ]);
      this.vendors      = vendorsRes.data?.data?.data  ?? vendorsRes.data?.data  ?? [];
      this.currencies   = currRes.data?.data?.data     ?? currRes.data?.data     ?? [];
      this.uoms         = uomRes.data?.data             ?? [];
      this.products     = productsRes.data?.data ?? [];
      this.locations    = locationsRes.data?.data ?? [];
      this.paymentTerms = termsRes.data?.data ?? [];

      if (this.isEdit) {
        await this.fetchPO();
      } else if (prId) {
        await this.loadFromPR(prId);
      }
    } catch (e) {
      this.toast.error('Failed to load form data');
    } finally {
      this.pageLoading = false;
    }
  },

  methods: {
    onPaymentTermChange() {
      const term = this.paymentTerms.find(t => t.id === this.form.payment_term_id);
      if (term) this.form.payment_terms = term.name;
    },

    availableQty(line) {
      return Math.max(0, parseFloat(line.quantity || 0) - parseFloat(line.ordered_qty || 0));
    },

    prLineTotal(line) {
      if (!this.selectedIds.includes(line.id)) return 0;
      const qty   = parseFloat(this.lineQtys[line.id] ?? this.availableQty(line));
      const price = parseFloat(this.linePrices[line.id] ?? line.estimated_unit_cost ?? 0);
      return qty * price;
    },

    lineStatusLabel(s) {
      return { available: 'Available', partially_ordered: 'Partial', ordered: 'Ordered' }[s] ?? 'Available';
    },

    toggleLine(line) {
      if (line.status === 'ordered') return;
      const idx = this.selectedIds.indexOf(line.id);
      if (idx >= 0) {
        this.selectedIds.splice(idx, 1);
      } else {
        this.selectedIds.push(line.id);
        if (this.lineQtys[line.id] === undefined) {
          this.lineQtys[line.id] = this.availableQty(line);
        }
      }
    },

    capQty(line) {
      const max = this.availableQty(line);
      const cur = parseFloat(this.lineQtys[line.id] || 0);
      if (cur > max) this.lineQtys[line.id] = max;
      if (cur <= 0)  this.lineQtys[line.id] = 0.001;
    },

    async fetchPO() {
      const { data } = await axios.get(`/procurement/purchase-orders/${this.$route.params.id}`);
      const po = data.data;
      this.form = {
        title:                  po.title,
        vendor_id:              po.vendor_id ?? '',
        currency:               po.currency ?? 'NGN',
        expected_delivery_date: po.expected_delivery_date ?? '',
        payment_terms:          po.payment_terms ?? '',
        payment_term_id:        po.payment_term_id ?? '',
        delivery_address:       po.delivery_address ?? '',
        description:            po.description ?? '',
        notes:                  po.notes ?? '',
        tax_amount:             po.tax_amount ?? 0,
        tax_rate:               '',
        item_type:              po.item_type ?? 'goods',
        location_id:            po.location_id ?? null,
        pr_id:                  po.pr_id,
        po_number:              po.po_number,
        lines: po.lines.map(l => ({
          description:  l.description,
          specification: l.specification ?? '',
          quantity:     l.quantity,
          uom:          l.uom ?? '',
          unit_price:   l.unit_price,
          pr_line_id:   l.pr_line_id,
          product_id:   l.product_id ?? null,
          location_id:  l.location_id ?? null,
        })),
      };
    },

    async loadFromPR(prId) {
      const { data } = await axios.get(`/procurement/purchase-requisitions/${prId}`);
      const pr = data.data;
      this.sourcePR       = pr;
      this.prLines        = pr.lines ?? [];
      this.form.pr_id     = pr.id;
      this.form.title     = `PO — ${pr.pr_number} · ${pr.title}`;
      this.form.currency  = pr.currency ?? 'NGN';
      this.form.item_type = pr.item_type ?? 'goods';

      if (pr.location) {
        const parts = [pr.location.store_name, pr.location.address].filter(Boolean);
        this.form.delivery_address = parts.join('\n');
      }

      // Pre-select all available (non-fully-ordered) lines
      this.selectedIds = this.prLines
        .filter(l => l.status !== 'ordered')
        .map(l => l.id);

      // Initialize qty, price, and location for each line
      this.lineQtys      = {};
      this.linePrices    = {};
      this.lineLocations = {};
      this.prLines.forEach(l => {
        this.lineQtys[l.id]      = this.availableQty(l);
        this.linePrices[l.id]    = parseFloat(l.estimated_unit_cost || 0);
        this.lineLocations[l.id] = pr.location_id ?? undefined;
      });
    },

    onLineProductChange(line, val) {
      const product = this.productOptions.find(p => p.value === (val ?? line.product_id));
      if (!product) return;
      line.description = product.name;
      if (!line.uom?.trim()) line.uom = product.uom || '';
    },
    applyHeaderLocation(val) {
      const loc = this.locationOptions.find(l => l.value === (val ?? this.form.location_id));
      if (loc) {
        this.form.lines.forEach(line => { line.location_id = loc.value; });
        const parts = [loc.name, loc.address].filter(Boolean);
        this.form.delivery_address = parts.join('\n');
      }
    },
    addLine()       { this.form.lines.push(emptyLine()); },
    removeLine(i)   { if (this.form.lines.length > 1) this.form.lines.splice(i, 1); },
    recalcLine()    { /* reactivity handles totals */ },
    lineTotal(line) { return (Number(line.quantity) || 0) * (Number(line.unit_price) || 0); },
    formatNum(v)    {
      const n = parseFloat(v);
      const amount = isNaN(n) ? 0 : n;
      const [int, dec] = amount.toFixed(2).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + dec;
    },

    validate() {
      this.errors     = {};
      this.lineErrors = [];
      let ok = true;

      if (this.isFromPR) {
        if (!this.selectedIds.length)  { this.errors.lines     = 'Select at least one item'; ok = false; }
        if (!this.form.vendor_id)      { this.errors.vendor_id = 'Vendor is required'; ok = false; }
      } else {
        if (!this.form.title?.trim())  { this.errors.title = 'Title is required'; ok = false; }
        if (!this.form.lines.length)   { this.errors.lines = 'Add at least one line item'; ok = false; }
        this.form.lines.forEach((l, i) => {
          const le = {};
          if (!l.description?.trim())    { le.description = 'Required'; ok = false; }
          if (!(Number(l.quantity) > 0)) { le.quantity = 'Must be > 0'; ok = false; }
          if (this.isGoods && !l.product_id) { le.product_id = 'Required for Goods'; ok = false; }
          this.lineErrors[i] = le;
        });
      }
      return ok;
    },

    async savePO() {
      if (!this.validate()) return;
      this.saving = 'save';
      try {
        let lines;
        if (this.isFromPR) {
          lines = this.selectedPRLines.map(l => ({
            description:   l.description,
            specification: l.specification || null,
            quantity:      parseFloat(this.lineQtys[l.id] ?? this.availableQty(l)),
            uom:           l.uom || null,
            unit_price:    parseFloat(this.linePrices[l.id] ?? l.estimated_unit_cost ?? 0),
            pr_line_id:    l.id,
            product_id:    l.procurement_product_id || null,
            location_id:   this.lineLocations[l.id] || null,
          }));
        } else {
          lines = this.form.lines.map(l => ({
            description:   l.description,
            specification: l.specification || null,
            quantity:      l.quantity,
            uom:           l.uom || null,
            unit_price:    l.unit_price,
            pr_line_id:    l.pr_line_id || null,
            product_id:    l.product_id || null,
            location_id:   l.location_id || null,
          }));
        }

        const payload = {
          title:                  this.isFromPR
                                    ? `PO — ${this.sourcePR?.pr_number}${this.form.vendor_id ? '' : ''}`
                                    : this.form.title,
          vendor_id:              this.form.vendor_id || null,
          currency:               this.form.currency,
          expected_delivery_date: this.form.expected_delivery_date || null,
          payment_terms:          this.form.payment_terms || null,
          payment_term_id:        this.form.payment_term_id || null,
          delivery_address:       this.form.delivery_address || null,
          description:            this.form.description || null,
          notes:                  this.form.notes || null,
          tax_amount:             this.computedTax,
          item_type:              this.form.item_type || 'goods',
          location_id:            this.form.location_id || null,
          pr_id:                  this.form.pr_id || null,
          lines,
        };

        // Use form.title for PR mode (auto-set in loadFromPR, user can't edit it currently)
        if (this.isFromPR) payload.title = this.form.title;

        let po;
        if (this.isEdit) {
          const { data } = await axios.put(`/procurement/purchase-orders/${this.$route.params.id}`, payload);
          po = data.data;
          this.toast.success('Purchase order updated');
        } else {
          const { data } = await axios.post('/procurement/purchase-orders', payload);
          po = data.data;
          this.toast.success('Purchase order created');
        }
        this.$router.replace(`/procurement/purchase-orders/${po.id}`);
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to save');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.back-row  { margin-bottom:12px; }
.btn-back  { background:transparent; border:none; color:#7a90b0; font-size:13px; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:7px; padding:0; }
.btn-back:hover { color:#004AAD; }
.page-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:20px; gap:12px; flex-wrap:wrap; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.mono        { font-family:monospace; font-size:13px; color:#5a6a85; background:#f4f7fb; padding:4px 10px; border-radius:6px; }
.loading-state { display:flex; flex-direction:column; align-items:center; padding:60px 24px; color:#7a90b0; gap:10px; }
.spinner    { width:32px; height:32px; border:3px solid #e8edf5; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }
.form-card  { background:#fff; border-radius:12px; border:1.5px solid #e8edf5; padding:22px 24px; margin-bottom:16px; }
.section-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; }
.section-title  { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 16px; }
.section-header .section-title { margin:0; }
.selected-count { font-size:13px; font-weight:600; color:#004AAD; background:#eff6ff; padding:3px 10px; border-radius:20px; }
.fields-grid    { display:grid; grid-template-columns:1fr 1fr; gap:14px 20px; }
.field-full     { grid-column:1/-1; }
.field-group    { display:flex; flex-direction:column; gap:5px; }
.field-label    { font-size:12.5px; font-weight:600; color:#5a6a85; }
.req            { color:#e53e3e; }
.field-input    { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; }
.field-input:focus { border-color:#004AAD; }
.field-input--error { border-color:#e53e3e; }
.field-select   { appearance:none; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%235a6a85'%3E%3Cpath fill-rule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 10px center; background-size:16px; padding-right:32px; }
.field-textarea { resize:vertical; min-height:60px; }
.field-error    { font-size:12px; color:#e53e3e; }
.mb-8           { margin-bottom:8px; }

/* Lines table */
.btn-add-line   { display:inline-flex; align-items:center; gap:5px; background:#f0f5ff; color:#004AAD; border:1.5px solid #c7d9f9; font-size:12.5px; font-weight:600; padding:6px 14px; border-radius:7px; cursor:pointer; }
.btn-add-line:hover { background:#dbeafe; }
.lines-table-wrap { overflow-x:auto; }
.lines-table    { width:100%; border-collapse:collapse; font-size:13px; }
.lines-table th { background:#f4f7fb; font-weight:600; color:#5a6a85; padding:9px 10px; text-align:left; font-size:12px; white-space:nowrap; }
.lines-table td { padding:8px 10px; border-top:1px solid #f0f4fa; vertical-align:middle; color:#1a2b45; }
.th-check, .td-check { width:36px; text-align:center; }
.td-qty   { width:120px; }
.td-price { width:120px; }
.cb      { width:15px; height:15px; cursor:pointer; accent-color:#004AAD; }
.text-right { text-align:right; }
.cell-input  { width:100%; padding:6px 8px; border:1.5px solid #dde5f0; border-radius:6px; font-size:13px; color:#1a2b45; background:#fff; outline:none; box-sizing:border-box; }
.cell-input:focus { border-color:#004AAD; }
.cell-input--error { border-color:#e53e3e; }
.cell-num    { text-align:right; font-variant-numeric:tabular-nums; }
.cell-total  { text-align:right; font-variant-numeric:tabular-nums; font-weight:600; color:#1a2b45; padding-right:12px; }
.btn-remove-line { background:none; border:none; color:#e53e3e; cursor:pointer; font-size:13px; padding:2px 6px; }
.btn-remove-line:hover { color:#9b1c1c; }
.tax-input-group  { display:inline-flex; align-items:center; gap:0; }
.tax-rate-input   { width:80px !important; border-radius:6px 0 0 6px !important; }
.tax-pct-sign     { padding:6px 9px; background:#f4f7fb; border:1.5px solid #dde5f0; border-left:none; border-radius:0 6px 6px 0; font-size:13px; font-weight:700; color:#5a6a85; line-height:1; }
.tfoot-row   { background:#f9fbff; }
.tfoot-total-row { background:#eef4ff; }
.tfoot-label { text-align:right; font-size:12.5px; font-weight:600; color:#5a6a85; padding:8px 12px; }
.tfoot-val   { text-align:right; font-variant-numeric:tabular-nums; font-weight:700; color:#1a2b45; padding:8px 12px; }
.tfoot-grand { font-size:14px; color:#004AAD; }
.tfoot-tax-row { display:flex; align-items:center; gap:8px; justify-content:flex-end; }
.tfoot-label-inline { font-size:12.5px; font-weight:600; color:#5a6a85; white-space:nowrap; }
.empty-lines { padding:32px 16px; text-align:center; color:#7a90b0; font-size:13px; }

.cell-select  { appearance:none; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%235a6a85'%3E%3Cpath fill-rule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 6px center; background-size:14px; padding-right:24px; }
.cell-select--locked { background-color:#f4f7fb; color:#7a90b0; cursor:not-allowed; opacity:.8; }
.cell-product { min-width:160px; }
.cell-multiselect { --ms-font-size:13px; --ms-line-height:1.4; --ms-min-height:34px; --ms-border-color:#dde5f0; --ms-border-radius:6px; --ms-ring-color:rgba(0,74,173,.2); --ms-dropdown-max-height:200px; }
.cell-multiselect--error { --ms-border-color:#e53e3e; }
.line-loc-row td { background:#f9fbff; border-top:none; padding:4px 10px 8px; }
.loc-label-cell { vertical-align:middle; }
.loc-label   { font-size:11.5px; color:#7a90b0; font-weight:600; }

/* PR mode row styles */
.row-ordered { background:#f9fafb; opacity:.65; }
.row-selected { background:#f0f7ff; }
.row-selected:hover { background:#e8f2ff; }
.item-desc { font-weight:600; color:#1a2b45; }
.item-spec { font-size:12px; color:#7a90b0; margin-top:2px; }
.qty-dim   { color:#9aa6b9; font-size:12.5px; }

/* Line status badges */
.line-status { display:inline-block; font-size:11px; font-weight:700; border-radius:20px; padding:2px 8px; white-space:nowrap; }
.line-status--available        { background:#dcfce7; color:#166534; }
.line-status--partially_ordered{ background:#fef3c7; color:#92400e; }
.line-status--ordered          { background:#e0e7ff; color:#3730a3; }

/* PR mode totals */
.totals-card { padding:16px 24px; }
.totals-row  { display:flex; align-items:center; justify-content:space-between; padding:6px 0; border-bottom:1px solid #f0f4fa; }
.totals-row:last-child { border-bottom:none; }
.totals-label { font-size:13px; color:#5a6a85; font-weight:600; }
.totals-val   { font-size:14px; font-variant-numeric:tabular-nums; font-weight:700; color:#1a2b45; }
.totals-grand-row { padding-top:10px; margin-top:4px; }
.totals-label-grand { font-size:14px; font-weight:700; color:#1a2b45; }
.totals-val-grand   { font-size:16px; font-weight:800; color:#004AAD; }
.tax-inline-input   { padding:6px 8px; border:1.5px solid #dde5f0; border-radius:6px 0 0 6px; font-size:13px; color:#1a2b45; width:80px; outline:none; text-align:right; }
.tax-inline-input:focus { border-color:#004AAD; }
.totals-tax-right   { display:flex; align-items:center; gap:10px; }
.totals-tax-computed{ font-size:13px; font-weight:700; color:#1a2b45; font-variant-numeric:tabular-nums; white-space:nowrap; }

/* Quill editor */
.quill-wrap { border: 1.5px solid #dde5f0; border-radius: 8px; overflow: hidden; }
.quill-wrap:focus-within { border-color: #004AAD; }
.quill-wrap :deep(.ql-toolbar) { border: none; border-bottom: 1.5px solid #dde5f0; background: #f8fafc; padding: 6px 10px; }
.quill-wrap :deep(.ql-container) { border: none; font-size: 13px; font-family: inherit; }
.quill-wrap :deep(.ql-editor) { min-height: 90px; padding: 9px 12px; color: #1a2b45; }
.quill-wrap :deep(.ql-editor.ql-blank::before) { color: #aab8cc; font-style: normal; left: 12px; }

/* Actions */
.form-actions   { display:flex; justify-content:flex-end; gap:10px; margin-top:4px; }
.btn-primary    { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13.5px; font-weight:600; padding:11px 24px; border-radius:9px; border:none; cursor:pointer; }
.btn-primary:hover:not(:disabled) { background:#003b8e; }
.btn-primary:disabled { opacity:.5; cursor:not-allowed; }
.btn-outline    { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13.5px; font-weight:600; padding:11px 20px; border-radius:9px; cursor:pointer; }
.btn-outline:hover:not(:disabled) { background:#f4f7fb; }
.spinner-sm { width:14px; height:14px; border:2px solid rgba(255,255,255,.3); border-top-color:#fff; border-radius:50%; animation:spin .6s linear infinite; }
</style>
